import { computed, SetupContext } from '@vue/composition-api'
import { OfficeInfo, StateType } from '@/@types/office/index'
import { defineObject } from '@/lib/define/use-define'

export const useComputed = (
  state: Pick<
    StateType,
    | 'prefecture'
    | 'viewOfficeNumber'
    | 'address'
    | 'city'
    | 'block'
    | 'building'
  > & {
    officeParam: {
      dataObject: any
    }
  }
) => {
  const getAddress = computed(() => {
    if (state.officeParam.dataObject.prefecture !== '選択してください') {
      state.prefecture = state.officeParam.dataObject.prefecture as string
    }
    if (state.officeParam.dataObject.city !== null) {
      state.city = state.officeParam.dataObject.city as string
    }
    if (state.officeParam.dataObject.block !== null) {
      state.block = state.officeParam.dataObject.block as string
    }
    if (state.officeParam.dataObject.building !== null) {
      state.building = state.officeParam.dataObject.building as string
    }
    state.address = `${state.prefecture}${state.city}${state.block}${state.building}`
    return state.address
  })

  const getOfficeNumber = computed(() => {
    if (state.viewOfficeNumber) {
      return String(state.viewOfficeNumber).slice(0, 10)
    } else {
      return ''
    }
  })

  const getOfficeUnits = computed(() => {
    if (state.viewOfficeNumber && String(state.viewOfficeNumber).length > 10) {
      return String(state.viewOfficeNumber).substr(-1, 1)
    } else {
      return ''
    }
  })

  const getJihatsuKasanBaseUp = computed(() => {
    if (state.officeParam.dataObject.jihatsuKasanWelfareCare !== '0') {
      return false
    } else {
      state.officeParam.dataObject.jihatsuKasanBaseUp = '0'
      return true
    }
  })

  const getKasanBaseUp = computed(() => {
    if (state.officeParam.dataObject.kasanTreatment !== '0') {
      return false
    } else {
      state.officeParam.dataObject.kasanBaseUp = '0'
      return true
    }
  })

  const getVisitKasanBaseUp = computed(() => {
    if (state.officeParam.dataObject.visitKasanWelfareCare !== '0') {
      return false
    } else {
      state.officeParam.dataObject.visitKasanBaseUp = '0'
      return true
    }
  })

  return {
    getAddress,
    getOfficeNumber,
    getOfficeUnits,
    getJihatsuKasanBaseUp,
    getKasanBaseUp,
    getVisitKasanBaseUp,
  }
}

export const useSelectValue = (
  state: Pick<
    StateType,
    | 'prefecture'
    | 'viewOfficeNumber'
    | 'address'
    | 'city'
    | 'block'
    | 'building'
    | 'errorList'
  > & {
    officeParam: {
      dataObject: any
    }
  },
  context: SetupContext
) => {
  const handleAutoAddress = (value: any) => {
    if (value === null) return
    state.officeParam.dataObject.prefecture =
      defineObject.PREFECTURE_OPTION_ARRAY[Number(value.prefcode)].prefName
    state.officeParam.dataObject.city = `${value.ja.address1}${value.ja.address2}`
  }
  const handlePrefectureInput = (value: string) => {
    if (value === null) return
    state.officeParam.dataObject.prefecture = value
  }
  const handleInputValue = (key: string, inputValue: any) => {
    state.officeParam.dataObject[key] = inputValue

    if (key === 'kyoseigataService') {
      if (inputValue === '0')
        state.officeParam.dataObject.kyoseigataServiceDetail = '0'
    }
  }

  const handleInputOnOffValue = (key: string, value: boolean) => {
    switch (key) {
      case 'kasanRestraint':
      case 'jihatsuKasanRestraint':
      case 'visitKasanRestraint':
        state.officeParam.dataObject.kasanRestraint = value
        state.officeParam.dataObject.jihatsuKasanRestraint = value
        state.officeParam.dataObject.visitKasanRestraint = value
        break

      case 'gensanGyakutai':
      case 'jihatsuGensanGyakutai':
      case 'visitGensanGyakutai':
        state.officeParam.dataObject.gensanGyakutai = value
        state.officeParam.dataObject.jihatsuGensanGyakutai = value
        state.officeParam.dataObject.visitGensanGyakutai = value
        break

      case 'gensanJohoKohyo':
      case 'jihatsuGensanJohoKohyo':
      case 'visitGensanJohoKohyo':
        state.officeParam.dataObject.gensanJohoKohyo = value
        state.officeParam.dataObject.jihatsuGensanJohoKohyo = value
        state.officeParam.dataObject.visitGensanJohoKohyo = value
        break

      case 'gensanJikoHyoka':
      case 'jihatsuGensanJikoHyoka':
      case 'visitGensanJikoHyoka':
        state.officeParam.dataObject.gensanJikoHyoka = value
        state.officeParam.dataObject.jihatsuGensanJikoHyoka = value
        state.officeParam.dataObject.visitGensanJikoHyoka = value
        break

      case 'gensanGyomuKeizoku':
      case 'jihatsuGensanGyomuKeizoku':
      case 'visitGensanGyomuKeizoku':
        state.officeParam.dataObject.gensanGyomuKeizoku = value
        state.officeParam.dataObject.jihatsuGensanGyomuKeizoku = value
        state.officeParam.dataObject.visitGensanGyomuKeizoku = value
        break

      case 'gensanShienPuroguramu':
      case 'jihatsuGensanShienPuroguramu':
        state.officeParam.dataObject.gensanShienPuroguramu = value
        state.officeParam.dataObject.jihatsuGensanShienPuroguramu = value
        break

      case 'kasanChukakuKino':
      case 'jihatsuKasanChukakuKino':
        state.officeParam.dataObject.kasanChukakuKino = value
        state.officeParam.dataObject.jihatsuKasanChukakuKino = value
        break

      default:
        state.officeParam.dataObject[key] = value
        break
    }
  }

  const handleAccountInputOnOffValue = (key: string, value: string) => {
    state.officeParam.dataObject[key] = value

    // 口座振替の利用がOFFであれば口座情報を空にする
    if (state.officeParam.dataObject.useDirectDebitFlg === 0) {
      state.officeParam.dataObject.bankType = ''
      state.officeParam.dataObject.transactionBankNumber = ''
      state.officeParam.dataObject.transactionBankName = ''
      state.officeParam.dataObject.transactionBankBranchNumber = ''
      state.officeParam.dataObject.transactionBankBranchName = ''
      state.officeParam.dataObject.transactionDepositType = ''
      state.officeParam.dataObject.transactionAccountNumber = ''
      state.officeParam.dataObject.entrustorCode = ''
      state.officeParam.dataObject.entrustorName = ''
      state.officeParam.dataObject.withdrawalPeriod = ''
      state.officeParam.dataObject.withdrawalDay = ''
      // バリデーションエラーもリセット
      delete state.errorList['bankType']
      delete state.errorList['transactionBankNumber']
      delete state.errorList['transactionBankName']
      delete state.errorList['transactionBankBranchNumber']
      delete state.errorList['transactionBankBranchName']
      delete state.errorList['transactionDepositType']
      delete state.errorList['transactionAccountNumber']
      delete state.errorList['entrustorCode']
      delete state.errorList['entrustorName']
      delete state.errorList['withdrawalPeriod']
      delete state.errorList['withdrawalDay']
    }

    // 銀行の種別がゆうちょでなければ空にする
    if (state.officeParam.dataObject.bankType !== 2) {
      state.officeParam.dataObject.transactionAccountIdCode = ''
      state.officeParam.dataObject.transactionAccountSymbol = ''
      // バリデーションエラーもリセット
      delete state.errorList['transactionAccountIdCode']
      delete state.errorList['transactionAccountSymbol']
    }
  }

  const handleTimeSet = (key: string, value: string) => {
    state.officeParam.dataObject[key] = value
  }

  const handleTimeErrorSet = (key: string, errorTimeMessage: string) => {
    if (errorTimeMessage) {
      context.root.$set(state.errorList, key, [errorTimeMessage])
    } else {
      context.root.$delete(state.errorList, key)
    }
  }

  const handleValueNumberInput = (
    key: string,
    event: Event,
    maxLength: null | number | string | undefined
  ) => {
    // Eventはinterfaceなのでvalueがない。
    // instanceofでHTMLInputElementをということを明示的にする
    if (!(event.target instanceof HTMLInputElement)) return

    if (event.target.value === '') return

    if (maxLength) {
      event.target.value = event.target.value.slice(0, Number(maxLength))
    }
    if (event.target.value.replace(/[^0-9]/g, '') === '') {
      state.officeParam.dataObject[key] = ''
      return
    }

    state.officeParam.dataObject[key] = Number(
      event.target.value.replace(/[^0-9]/g, '')
    )
  }

  const handleInputCheckOnly = (key: string, value: boolean) => {
    if (state.officeParam.dataObject[key] !== value) {
      state.officeParam.dataObject[key] = value
    } else {
      state.officeParam.dataObject[key] = ''
    }

    if (
      Array.isArray(state.officeParam.dataObject[key]) &&
      state.officeParam.dataObject[key].length > 1
    ) {
      state.officeParam.dataObject[key].shift()
    }

    context.root.$set(
      state.officeParam.dataObject,
      key,
      state.officeParam.dataObject[key]
    )

    if (
      (key === 'jihatsuKasanChukakuKinoOnlyCenter' ||
        key === 'jihatsuKasanMealOffer') &&
      state.officeParam.dataObject[key] === ''
    ) {
      state.officeParam.dataObject[key] = '0'
    }
  }

  const handleCheckedAffiliation = (key: string, value: any) => {
    const removeArray = []
    if (key === 'developmentSupportCenterFlg') {
      if (value === '0') {
        if (state.officeParam.dataObject.localGovernmentFlg !== '0')
          removeArray.push('地方公共団体')
        if (
          state.officeParam.dataObject.jihatsuKasanChukakuKinoOnlyCenter !== '0'
        )
          removeArray.push('児童発達支援センターでの中核機能強化加算')
        if (state.officeParam.dataObject.jihatsuKasanMealOffer !== '0')
          removeArray.push('食事提供加算')
        if (removeArray.length !== 0) {
          alert(
            `事業形態がセンターではないため、${removeArray.join(
              '、'
            )}のチェックが外れます。`
          )
          state.officeParam.dataObject.localGovernmentFlg = '0'
          state.officeParam.dataObject.jihatsuKasanChukakuKinoOnlyCenter = '0'
          state.officeParam.dataObject.jihatsuKasanMealOffer = '0'
        }
      }
      if (value === '1') {
        if (state.officeParam.dataObject.affiliation.includes('0'))
          removeArray.push('放デイ')
        if (state.officeParam.dataObject.affiliation.includes('2'))
          removeArray.push('保訪')
        if (state.officeParam.dataObject.jihatsuKasanChukakuKino === '1')
          removeArray.push('児童発達支援センター以外での中核機能強化事業所加算')
        if (state.officeParam.dataObject.kyoseigataService !== '0')
          removeArray.push('共生型サービス')
        if (state.officeParam.dataObject.kyoseigataServiceDetail !== '0')
          removeArray.push('共生型サービス体制強化加算')

        if (removeArray.length !== 0) {
          alert(
            `事業形態が児童発達支援センターのため、${removeArray.join(
              '、'
            )}のチェックが外れます。${
              !state.officeParam.dataObject.affiliation.includes('1')
                ? '\nまた、児童発達が選択されます。'
                : ''
            }`
          )
          const newAffiliationArray = state.officeParam.dataObject.affiliation.filter(
            (af: string) => af === '1'
          )
          if (!newAffiliationArray.includes('1')) newAffiliationArray.push('1')
          state.officeParam.dataObject.affiliation = newAffiliationArray
          state.officeParam.dataObject.jihatsuKasanChukakuKino = '0'
          state.officeParam.dataObject.kasanChukakuKino = '0'
          state.officeParam.dataObject.kyoseigataService = '0'
          state.officeParam.dataObject.kyoseigataServiceDetail = '0'
        }
      }
    }
    handleInputOnOffValue(key, value)
  }

  return {
    handleAutoAddress,
    handlePrefectureInput,
    handleInputValue,
    handleInputOnOffValue,
    handleAccountInputOnOffValue,
    handleTimeSet,
    handleTimeErrorSet,
    handleValueNumberInput,
    handleInputCheckOnly,
    handleCheckedAffiliation,
  }
}

export const useUtils = (state: any) => {
  const handleSelectStaffId = (staffId: string, staffName: string) => {
    state.officeParam.dataObject.lastUpdatedByStaffId = staffId
    state.officeParam.dataObject.lastUpdatedByStaffName = staffName
  }

  const handleUnitData = (unitDataArray: any[]) => {
    const officeId = document.cookie.replace(
      /(?:(?:^|.*;\s*)office_id\s*=\s*([^;]*).*$)|^.*$/,
      '$1'
    )
    const unitOfficeArray = unitDataArray.filter(
      (element: any) => element.office_id !== officeId
    )
    return unitOfficeArray
  }

  const handleGetUnitsNumber = (unitDataArray: any[]) => {
    unitDataArray.forEach(e => {
      e.officeGroup = String(e.officeNumber).substr(-1, 1)
    })
    return unitDataArray
  }

  const handleFormatUnitArray = (array: any[]) => {
    array.sort((a, b) => Number(a.officeGroup) - Number(b.officeGroup))
    return array
  }

  return {
    handleSelectStaffId,
    handleUnitData,
    handleGetUnitsNumber,
    handleFormatUnitArray,
  }
}

export const handleCheckBillingOfficeData = (
  officeData: OfficeInfo,
  isCsvBilling = false
) => {
  const officeDataArray = []

  if (!officeData) return []

  const billingAreaClass = officeData.billingAreaClass
    ? officeData.billingAreaClass
    : officeData.billing_area_class

  const areaClassLevel = officeData.areaClassLevel
    ? officeData.areaClassLevel
    : officeData.area_class_level

  if (billingAreaClass === '' || billingAreaClass === undefined) {
    officeDataArray.push('級地情報')
  }

  if (
    (officeData.affiliation.includes('0') ||
      officeData.affiliation.includes('1')) &&
    (areaClassLevel === '' || areaClassLevel === undefined)
  ) {
    officeDataArray.push('級地情報（レベル）')
  }

  if (
    (officeData.affiliation.includes('0') ||
      officeData.affiliation.includes('1')) &&
    (Number(officeData.capacity) === 0 || officeData.capacity === undefined)
  ) {
    officeDataArray.push('定員')
  }

  if (
    officeData.affiliation.includes('0') &&
    (officeData.kasan_classification === '' ||
      officeData.kasan_classification === undefined)
  ) {
    officeDataArray.push('区分')
  }

  if (!isCsvBilling) {
    // 営業時間未設定制御
    if (!officeData.start_time || !officeData.end_time) {
      officeDataArray.push('営業時間')
    }

    // サービス提供時間未設定制御(放デイ)
    if (officeData.affiliation.includes('0')) {
      if (!officeData.service_start_time || !officeData.service_end_time) {
        officeDataArray.push('放課後等デイサービスのサービス提供時間')
      }
    }

    // サービス提供時間未設定制御(児発)
    if (officeData.affiliation.includes('1')) {
      if (
        !officeData.jihatsu_service_start_time ||
        !officeData.jihatsu_service_end_time
      ) {
        officeDataArray.push('児童発達支援のサービス提供時間')
      }
    }
  }

  return officeDataArray
}

export const handleCheckBillingOfficeDataForRevision2024 = (
  officeData: OfficeInfo,
  isCsvBilling = false
) => {
  const officeDataArray = []

  if (!officeData) return []

  const billingAreaClass = officeData.billingAreaClass
    ? officeData.billingAreaClass
    : officeData.billing_area_class

  const areaClassLevel = officeData.areaClassLevel
    ? officeData.areaClassLevel
    : officeData.area_class_level

  if (billingAreaClass === '' || billingAreaClass === undefined) {
    officeDataArray.push('級地情報')
  }

  if (
    (officeData.affiliation.includes('0') ||
      officeData.affiliation.includes('1')) &&
    (areaClassLevel === '' || areaClassLevel === undefined)
  ) {
    officeDataArray.push('級地情報（レベル）')
  }

  if (
    ((officeData.affiliation.includes('0') ||
      officeData.affiliation.includes('1')) &&
      (Number(officeData.capacity) === 0 ||
        officeData.capacity === undefined) &&
      officeData.tokurei_flg === '1') ||
    officeData.tokurei_flg === undefined ||
    officeData.tokurei_flg === ''
  ) {
    officeDataArray.push('定員')
  }

  if (
    (Number(officeData.jihatsu_capacity) === 0 ||
      officeData.jihatsu_capacity === undefined) &&
    officeData.tokurei_flg === '0'
  ) {
    officeDataArray.push('児童発達支援の定員')
  }

  if (
    (Number(officeData.hodei_capacity) === 0 ||
      officeData.hodei_capacity === undefined) &&
    officeData.tokurei_flg === '0'
  ) {
    officeDataArray.push('放課後等デイサービスの定員')
  }

  if (!isCsvBilling) {
    // 営業時間未設定制御
    if (!officeData.start_time || !officeData.end_time) {
      officeDataArray.push('営業時間')
    }

    // サービス提供時間未設定制御(放デイ)
    if (officeData.affiliation.includes('0')) {
      if (!officeData.service_start_time || !officeData.service_end_time) {
        officeDataArray.push('放課後等デイサービスのサービス提供時間')
      }
    }

    // サービス提供時間未設定制御(児発)
    if (officeData.affiliation.includes('1')) {
      if (
        !officeData.jihatsu_service_start_time ||
        !officeData.jihatsu_service_end_time
      ) {
        officeDataArray.push('児童発達支援のサービス提供時間')
      }
    }
  }

  return officeDataArray
}

export const handleJudgeNewKasanShoguKaizen = (year: number, month: number) => {
  let isNewKasan = false
  const NEW_KASAN_SHOGU_KAIZEN_START_YEAR = 2024
  const NEW_KASAN_SHOGU_KAIZEN_START_MONTH = 6

  const targetDate = new Date(year, month - 1)
  const newKasanDate = new Date(
    NEW_KASAN_SHOGU_KAIZEN_START_YEAR,
    NEW_KASAN_SHOGU_KAIZEN_START_MONTH - 1
  )

  if (targetDate.getTime() >= newKasanDate.getTime()) {
    isNewKasan = true
  }
  return isNewKasan
}

export const handleResetKasanForReBilling = (
  state: Omit<
    StateType,
    | 'holidayAttendance'
    | 'defaultHolidayAttendance'
    | 'isOpenModal'
    | 'officeParam'
    | 'currentStaff'
    | 'lineParam'
    | 'toggleObject'
    | 'oemLogo'
    | 'alertParam'
    | 'prevUseAlertFlg'
    | 'isCodomon'
  > & {
    officeParam: any
    officeId: string
    isNewKasan: boolean
  }
) => {
  if (state.isNewKasan) {
    // 令和６年6月以降は旧処遇改善加算をリセット
    state.officeParam.dataObject.kasanTreatment = '0'
    state.officeParam.dataObject.kasanSpecificTreatment = '0'
    state.officeParam.dataObject.kasanBaseUp = '0'
    state.officeParam.dataObject.jihatsuKasanWelfareCare = '0'
    state.officeParam.dataObject.jihatsuKasanSomeWelfareCare = '0'
    state.officeParam.dataObject.jihatsuKasanBaseUp = '0'
    state.officeParam.dataObject.visitKasanWelfareCare = '0'
    state.officeParam.dataObject.visitKasanSomeWelfareCare = '0'
    state.officeParam.dataObject.visitKasanBaseUp = '0'
  } else {
    // 令和６年5月以前は新処遇改善加算をリセット
    state.officeParam.dataObject.kasanShoguKaizen = '0'
    state.officeParam.dataObject.jihatsuKasanShoguKaizen = '0'
    state.officeParam.dataObject.visitKasanShoguKaizen = '0'
  }
}
